// CallStrip (calls round, DESIGN.md §9): the calls under the top bar, on every page, while one rings for
// you, you're on one, or one waits on hold; and the same grid as a list in Inbox › Calls (On calls now,
// To call back, Earlier today). One grid, so its CallRows' cells line up in columns whatever their kind:
// the mark, who's calling, what we know, the line, how long, the actions. On a phone each call stacks.
// What it says changes are announced: `announce` is read out politely, or at once when `urgent` (a call
// ringing for you). A list has a heading and a count; the strip names itself for screen readers only.
// Reads only --callstrip-*.
import type { ReactNode } from 'react';
import { CallRowPlace } from '../CallRow/CallRow.tsx';
import './CallStrip.css';

export interface CallStripProps {
  /** Its name ("Calls", "To call back"). A list shows it as its heading. */
  label: string;
  /** CallRows. */
  children: ReactNode;
  /** strip: under the top bar; list: a section of Inbox › Calls. */
  variant?: 'strip' | 'list';
  /** A list's count, after its heading. */
  count?: number;
  /** A count that wants doing (calls to return) is drawn on. */
  countHot?: boolean;
  /** What changed, read out ("Ringing: Marguerite Osei, Main line"). */
  announce?: string;
  /** Read out at once (a call ringing for you), not politely. */
  urgent?: boolean;
  /** Under a list: a link to more ("Show all 11"). */
  foot?: ReactNode;
}

export function CallStrip({
  label,
  children,
  variant = 'strip',
  count,
  countHot,
  announce,
  urgent,
  foot,
}: CallStripProps) {
  const head =
    variant === 'list' ? (
      <h2 className="fl-callstrip__head">
        {label}
        {count === undefined ? null : (
          <span className="fl-callstrip__count" data-hot={countHot ? '' : undefined}>
            {count}
          </span>
        )}
      </h2>
    ) : null;
  return (
    <section
      className="fl-callstrip"
      data-variant={variant}
      aria-label={variant === 'strip' ? label : undefined}
    >
      {head}
      <div className="fl-callstrip__grid" role="table" aria-label={label}>
        <div className="fl-callstrip__cols" role="row">
          <span role="columnheader">Caller</span>
          <span role="columnheader">Who it is</span>
          <span role="columnheader">Came in on</span>
          <span role="columnheader">Time</span>
          <span role="columnheader">Actions</span>
        </div>
        <CallRowPlace.Provider value={variant}>{children}</CallRowPlace.Provider>
      </div>
      {foot ? <p className="fl-callstrip__foot">{foot}</p> : null}
      <p className="fl-callstrip__said" role={urgent ? 'alert' : 'status'}>
        {announce}
      </p>
    </section>
  );
}
